import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    Chip,
    Avatar,
    Badge,
    Paper,
} from "@mui/material";
import {
    MyLocation as MyLocationIcon,
    Refresh as RefreshIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    Fullscreen as FullscreenIcon,
} from "@mui/icons-material";
import {
    useGetAllTrackingDataQuery,
    useGetUserTrackingDataQuery,
} from "../../../store/tracking/trackingApis";
import { googleMapsApiKey } from "../../../helpers/constants";
import Button from "../../Common/Button";

const LiveTrackingMap = ({ targetUserId, selectedUserFromList }) => {
    const mapRef = useRef(null);
    const markersRef = useRef({});
    const [map, setMap] = useState(null);
    const [mapDivReady, setMapDivReady] = useState(false);
    const [mapInitialized, setMapInitialized] = useState(false);
    const [userHasInteracted, setUserHasInteracted] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    // Fetch all tracking data (manual refresh only)
    const {
        data: trackingData,
        isLoading: trackingLoading,
        error: trackingError,
        refetch: refetchTrackingData,
    } = useGetAllTrackingDataQuery(undefined, {
        pollingInterval: 0, // No polling - manual refresh only
    });

    // Fetch specific user details if targetUserId is provided
    const { data: userTrackingData, isLoading: userLoading } = useGetUserTrackingDataQuery(targetUserId, {
        skip: !targetUserId,
        pollingInterval: 0, // No polling - manual refresh only
    });

    // Callback ref to know when the div is rendered
    const setMapRef = (element) => {
        mapRef.current = element;
        if (element) {
            setMapDivReady(true);
        } else {
            setMapDivReady(false);
        }
    };

    // Initialize map
    useEffect(() => {
        const initMap = () => {
            if (!mapRef.current) {
                return;
            }

            try {
                const mapInstance = new window.google.maps.Map(mapRef.current, {
                    center: { lat: 19.076, lng: 72.8777 }, // Mumbai default
                    zoom: 12,
                    mapTypeId: "roadmap",
                    styles: [
                        {
                            featureType: "poi",
                            elementType: "labels",
                            stylers: [{ visibility: "off" }],
                        },
                    ],
                });

                // Track user interactions with the map
                mapInstance.addListener("dragstart", () => {
                    setUserHasInteracted(true);
                });

                mapInstance.addListener("zoom_changed", () => {
                    setUserHasInteracted(true);
                });

                setMap(mapInstance);
                setMapInitialized(true);
            } catch (error) {
                console.error("Error initializing Google Maps:", error);
            }
        };

        // Only proceed if the map div is ready
        if (!mapDivReady) {
            return;
        }

        // Check if map ref exists and Google Maps is loaded
        if (mapRef.current && window.google && window.google.maps) {
            initMap();
        } else if (mapRef.current) {
            // Load Google Maps if not already loaded
            if (googleMapsApiKey) {
                const script = document.createElement("script");
                script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=geometry`;
                script.async = true;
                script.defer = true;
                script.onload = initMap;
                script.onerror = (error) => {
                    console.error("Failed to load Google Maps script:", error);
                };
                document.head.appendChild(script);
            }
        }
    }, [googleMapsApiKey, mapDivReady]);

    // Update markers when tracking data changes
    useEffect(() => {
        if (!map || !trackingData) return;

        console.log("Updating markers with tracking data:", trackingData);

        const connectedUsers = trackingData.connectedUsers || [];
        const userLocations = trackingData.userLocations || {};

        console.log("Connected users:", connectedUsers);
        console.log("User locations:", userLocations);

        // Track which users currently exist (use id or userId)
        const currentUserIds = new Set(connectedUsers.map((user) => (user?.id || user?.userId)?.toString()));

        // Remove markers for users who are no longer in the list
        Object.keys(markersRef.current).forEach((userId) => {
            if (!currentUserIds.has(userId) && !userId.includes("_path")) {
                console.log(`Removing marker for user: ${userId}`);
                const marker = markersRef.current[userId];
                if (marker && marker.setMap) {
                    marker.setMap(null);
                }
                delete markersRef.current[userId];

                // Also remove the corresponding path marker
                const pathKey = `${userId}_path`;
                const pathMarker = markersRef.current[pathKey];
                if (pathMarker && pathMarker.setMap) {
                    pathMarker.setMap(null);
                }
                delete markersRef.current[pathKey];
            }
        });

        // Update or create markers for each connected user
        connectedUsers.forEach((user) => {
            const userId = user?.id || user?.userId;
            // Try userLocations first, then currentLocation from user object
            const location = userLocations[userId] || userLocations[user?.id] || userLocations[user?.userId] || user?.currentLocation;
            console.log(`Processing user ${userId}:`, { location, user });

            if (location && location.latitude && location.longitude) {
                const position = {
                    lat: parseFloat(location.latitude),
                    lng: parseFloat(location.longitude),
                };

                // Check if marker already exists
                const existingMarker = markersRef.current[userId?.toString()];

                if (existingMarker && existingMarker.setPosition) {
                    // Update existing marker position
                    existingMarker.setPosition(position);

                    // Update marker icon
                    const newIcon = {
                        url: user?.isTracking
                            ? "data:image/svg+xml;charset=UTF-8," +
                              encodeURIComponent(`
                                <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="16" cy="16" r="12" fill="#4CAF50" stroke="white" stroke-width="2"/>
                                    <circle cx="16" cy="16" r="4" fill="white"/>
                                </svg>
                            `)
                            : "data:image/svg+xml;charset=UTF-8," +
                              encodeURIComponent(`
                                <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="16" cy="16" r="12" fill="#9E9E9E" stroke="white" stroke-width="2"/>
                                    <circle cx="16" cy="16" r="4" fill="white"/>
                                </svg>
                            `),
                        scaledSize: new window.google.maps.Size(32, 32),
                        anchor: new window.google.maps.Point(16, 16),
                    };
                    existingMarker.setIcon(newIcon);

                    // Update info window content
                    if (existingMarker.infoWindow) {
                        existingMarker.infoWindow.setContent(`
                            <div style="padding: 10px; min-width: 200px;">
                                <h3 style="margin: 0 0 8px 0; color: #333;">${
                                    typeof user?.name === "string" ? user?.name : userId
                                }</h3>
                                <p style="margin: 4px 0; color: #666;">
                                    <strong>Role:</strong> ${user?.role || "Staff"}
                                </p>
                                <p style="margin: 4px 0; color: #666;">
                                    <strong>Status:</strong> 
                                    <span style="color: ${user?.isTracking ? "#4CAF50" : "#9E9E9E"}">
                                        ${user?.isTracking ? "Has Location" : "No Location"}
                                    </span>
                                </p>
                                <p style="margin: 4px 0; color: #666;">
                                    <strong>Accuracy:</strong> ${location.accuracy || "N/A"}m
                                </p>
                                <p style="margin: 4px 0; color: #666;">
                                    <strong>Last Update:</strong> ${new Date(location.timestamp).toLocaleTimeString()}
                                </p>
                            </div>
                        `);
                    }
                } else {
                    // Create new marker
                    console.log(`Creating new marker for ${user?.name} at:`, position);

                    const marker = new window.google.maps.Marker({
                        position,
                        map,
                        title: user?.name || userId,
                        icon: {
                            url: user?.isTracking
                                ? "data:image/svg+xml;charset=UTF-8," +
                                  encodeURIComponent(`
                                <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="16" cy="16" r="12" fill="#4CAF50" stroke="white" stroke-width="2"/>
                                    <circle cx="16" cy="16" r="4" fill="white"/>
                                </svg>
                            `)
                                : "data:image/svg+xml;charset=UTF-8," +
                                  encodeURIComponent(`
                                <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="16" cy="16" r="12" fill="#9E9E9E" stroke="white" stroke-width="2"/>
                                    <circle cx="16" cy="16" r="4" fill="white"/>
                                </svg>
                            `),
                            scaledSize: new window.google.maps.Size(32, 32),
                            anchor: new window.google.maps.Point(16, 16),
                        },
                    });

                    // Create info window
                    const infoWindow = new window.google.maps.InfoWindow({
                        content: `
                        <div style="padding: 10px; min-width: 200px;">
                            <h3 style="margin: 0 0 8px 0; color: #333;">${
                                typeof user?.name === "string" ? user?.name : userId
                            }</h3>
                            <p style="margin: 4px 0; color: #666;">
                                <strong>Role:</strong> ${user?.role || "Staff"}
                            </p>
                            <p style="margin: 4px 0; color: #666;">
                                <strong>Status:</strong> 
                                <span style="color: ${user?.isTracking ? "#4CAF50" : "#9E9E9E"}">
                                    ${user?.isTracking ? "Has Location" : "No Location"}
                                </span>
                            </p>
                            <p style="margin: 4px 0; color: #666;">
                                <strong>Accuracy:</strong> ${location.accuracy || "N/A"}m
                            </p>
                            <p style="margin: 4px 0; color: #666;">
                                <strong>Last Update:</strong> ${new Date(location.timestamp).toLocaleTimeString()}
                            </p>
                        </div>
                    `,
                    });

                    // Store info window reference on marker
                    marker.infoWindow = infoWindow;

                    // Add click listener
                    marker.addListener("click", () => {
                        // Close all other info windows first
                        Object.values(markersRef.current).forEach((m) => {
                            if (m.infoWindow && m !== marker) {
                                m.infoWindow.close();
                            }
                        });

                        infoWindow.open(map, marker);
                        setSelectedUser(user);
                    });

                    // Store marker reference
                    markersRef.current[userId?.toString()] = marker;
                }
            }
        });

        // Center map on initial load or when user explicitly selects from list
        if (!userHasInteracted) {
            if (selectedUserFromList) {
                const selectedUserId = selectedUserFromList.id || selectedUserFromList.userId;
                const targetLocation = userLocations[selectedUserId] || selectedUserFromList.currentLocation;
                if (targetLocation) {
                    map.setCenter({
                        lat: parseFloat(targetLocation.latitude),
                        lng: parseFloat(targetLocation.longitude),
                    });
                    map.setZoom(15);

                    // Open info window for the selected user
                    const marker = markersRef.current[selectedUserId?.toString()];
                    if (marker && marker.infoWindow) {
                        marker.infoWindow.open(map, marker);
                        setSelectedUser(selectedUserFromList);
                    }
                }
            } else if (targetUserId && userTrackingData?.currentLocation && !mapInitialized) {
                const targetLocation = userTrackingData.currentLocation;
                map.setCenter({
                    lat: parseFloat(targetLocation.latitude),
                    lng: parseFloat(targetLocation.longitude),
                });
                map.setZoom(15);
            } else if (connectedUsers.length > 0 && !mapInitialized) {
                // Center map on first connected user if no target user (only on initial load)
                const firstUser = connectedUsers[0];
                const firstLocation = userLocations[firstUser.userId];
                if (firstLocation && firstLocation.latitude && firstLocation.longitude) {
                    console.log("Centering map on first user:", firstLocation);
                    map.setCenter({
                        lat: parseFloat(firstLocation.latitude),
                        lng: parseFloat(firstLocation.longitude),
                    });
                    map.setZoom(14);
                }
            }
        }
    }, [map, trackingData, targetUserId, userTrackingData, selectedUserFromList, mapInitialized, userHasInteracted]);

    // Reset user interaction state when selectedUserFromList changes
    useEffect(() => {
        if (selectedUserFromList) {
            setUserHasInteracted(false);
        }
    }, [selectedUserFromList]);

    // Map controls
    const handleZoomIn = () => {
        if (map) map.setZoom(map.getZoom() + 1);
    };

    const handleZoomOut = () => {
        if (map) map.setZoom(map.getZoom() - 1);
    };

    const handleFullscreen = () => {
        if (mapRef.current.requestFullscreen) {
            mapRef.current.requestFullscreen();
        }
    };

    if (trackingLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (trackingError) {
        return (
            <Alert severity="error" sx={{ mb: 3 }}>
                {trackingError?.data?.message || "Failed to load tracking data"}
            </Alert>
        );
    }

    // Show fallback if no API key
    if (!googleMapsApiKey) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "70vh",
                    bgcolor: "#f5f5f5",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0",
                }}
            >
                <Typography variant="h6" color="textSecondary">
                    Google Maps API key not configured
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ height: "70vh", position: "relative" }}>
            {/* Map Container */}
            <div
                ref={setMapRef}
                style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0",
                    minHeight: "400px",
                }}
            />
            {!map && (
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 1,
                    }}
                >
                    <CircularProgress />
                </Box>
            )}

            {/* Map Controls */}
            <Box
                sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                <Tooltip title="Zoom In">
                    <IconButton onClick={handleZoomIn} sx={{ bgcolor: "white", boxShadow: 2 }}>
                        <ZoomInIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Zoom Out">
                    <IconButton onClick={handleZoomOut} sx={{ bgcolor: "white", boxShadow: 2 }}>
                        <ZoomOutIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Fullscreen">
                    <IconButton onClick={handleFullscreen} sx={{ bgcolor: "white", boxShadow: 2 }}>
                        <FullscreenIcon />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Refresh">
                    <IconButton onClick={refetchTrackingData} sx={{ bgcolor: "white", boxShadow: 2 }}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Legend */}
            <Paper
                sx={{
                    position: "absolute",
                    bottom: 10,
                    left: 10,
                    p: 2,
                    bgcolor: "rgba(255,255,255,0.9)",
                    backdropFilter: "blur(10px)",
                }}
            >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Legend
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                bgcolor: "#4CAF50",
                            }}
                        />
                        <Typography variant="caption">Has Location Data</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                bgcolor: "#9E9E9E",
                            }}
                        />
                        <Typography variant="caption">No Location Data</Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Selected User Info */}
            {selectedUser && (
                <Card
                    sx={{
                        position: "absolute",
                        top: 10,
                        left: 10,
                        maxWidth: 300,
                        bgcolor: "rgba(255,255,255,0.95)",
                        backdropFilter: "blur(10px)",
                    }}
                >
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                            <Badge
                                color={selectedUser?.isTracking ? "success" : "error"}
                                variant="dot"
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "right",
                                }}
                            >
                                <Avatar sx={{ bgcolor: "#4CAF50" }}>
                                    {typeof selectedUser?.name === "string"
                                        ? selectedUser?.name.charAt(0)
                                        : selectedUser?.userId?.charAt(0)}
                                </Avatar>
                            </Badge>
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {typeof selectedUser?.name === "string"
                                        ? selectedUser?.name
                                        : selectedUser?.userId}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    {selectedUser?.role || "Staff"}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                            <Chip
                                label={selectedUser?.isTracking ? "Has Location" : "No Location"}
                                size="small"
                                color={selectedUser?.isTracking ? "success" : "default"}
                            />
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default LiveTrackingMap;
